import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import config from "config/config";
import { instance } from "api/apiBase";
import { CheckoutForm } from "./components/CheckoutForm";
import { CheckoutOptionsSelector } from "./components/CheckoutOptionsSelector";
import { ApplicationFeePaidView } from "./components/ApplicationFeePaidView";
import { InfoCircle } from "untitledui-js-base";
import { Step } from "../../types";
import { StepNames } from "../../constants";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import {
  Applicant,
  ApplicantStatus,
  ApplicantType,
  Application,
  ApplicationFeeStatus,
  CheckoutSubmissionType,
  SubmissionType
} from "shared/types";
import { useIntercom } from "react-use-intercom";
import { Loader } from "shared/components/Loader";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import { Clock01Icon } from "@hugeicons-pro/core-solid-rounded";
import { EmailPaymentPendingView } from "./components/EmailPaymentPendingView";
import { FreeCheckoutView } from "./components/FreeCheckoutView";
import templatesApi from "api/landlord/templates";
import { Template } from "types/landlord/verificationTemplate";

const stripe = loadStripe(config.stripePk || "");

interface PaymentIntent {
  client_secret: string | null;
  ephemeral_key: string | null;
  payment_intent_id: string | null;
  stripe_customer_id: string | null;
}

export const CheckoutSlice = ({
  applicant,
  applicantId,
  sendDisclosureEmail,
  isSubmissionDisclosureFinished,
  steps,
  setActiveStepName,
  hasIncomeSource,
  onSuccess,
  application
}: {
  applicant: Applicant;
  applicantId: number;
  steps?: Record<string, Step>;
  setActiveStepName: (stepName: string) => void;
  hasIncomeSource?: boolean;
  goBack: () => void;
  onSuccess: () => void;
  sendDisclosureEmail: boolean;
  isSubmissionDisclosureFinished: boolean;
  application: Application;
}) => {
  const intercom = useIntercom();
  const queryClient = useQueryClient();

  const { data: templateCosts, isLoading: isLoadingTemplateCosts } = useQuery({
    queryKey: ["FEE_DATA", application.verification_template.id],
    queryFn: () => {
      const template = application.verification_template as unknown as Template;

      if (!template.id) return Promise.resolve(null);

      return templatesApi.validateTemplate({
        ...template,
        applicant_paid: template["applicant_fee"].cents > 0,
        co_signer_paid: template["co_signer_fee"].cents > 0,
        occupant_paid: template["occupant_fee"].cents > 0
      });
    },
    select: (data) => {
      if (!data) return {};

      const costs =
        data.verification_template.costs?.[
          applicant.role || ApplicantType.Applicant
        ];

      return costs.reduce((acc, curr) => {
        acc[curr.name] = curr.amount;
        return acc;
      }, {});
    },
    enabled: Boolean(application.verification_template.id)
  });

  // Create payment intent (each call creates an invoice, so skip once submitted)
  const { data: paymentIntent, isLoading } = useQuery({
    queryKey: ["createPaymentIntent", applicantId],
    queryFn: () =>
      instance
        .post<PaymentIntent>(
          `/portal/v1/applicants/${applicantId}/checkout/create_payment_intent`
        )
        .then(({ data }) => data),
    enabled:
      Boolean(applicantId) &&
      Boolean(application.id) &&
      applicant.application_status !== ApplicantStatus.Submitted
  });

  // Checkout
  const checkOutMeta = useMutation({
    mutationKey: ["checkOut", applicantId],
    mutationFn: (data: { stripe_token: string }) =>
      instance.post(
        `/portal/v1/applicants/${applicantId}/checkout/with_stripe_payment_intent`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "applicationMeta",
          String(application.id),
          String(applicantId)
        ]
      });
    }
  });

  const freeCheckoutMeta = useMutation({
    mutationKey: ["freeCheckout", application.id, applicantId],
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${application.id}/applicants/${applicantId}/submit_application`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "applicationMeta",
          String(application.id),
          String(applicantId)
        ]
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const {
    mutateAsync: updatePreferredCheckoutType,
    isPending: isUpdatingPreferredCheckoutType
  } = useMutation({
    mutationKey: ["updatePreferredCheckoutType", application.id, applicantId],
    mutationFn: (data: {
      preferred_checkout_type: CheckoutSubmissionType | null;
      email?: string;
    }) =>
      instance.patch(
        `/portal/v1/applications/${application.id}/applicants/${applicantId}/update_preferred_manual_values`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "applicationMeta",
          String(application.id),
          String(applicantId)
        ]
      });
    }
  });

  // If some steps are unfished show the checkout error
  const unfinishedSteps = Object.entries(steps || {}).reduce((acc, s) => {
    const step = s[1];

    // Ignore income_documents
    if (!hasIncomeSource) if (s[0] === "income_documents") return acc;

    if (s[0] === "move_in_date") {
      if (!application.desired_move_in_date) acc[s[0]] = step;
      return acc;
    }

    if (!Object.keys(step.data).length && s[0] !== "checkout") acc[s[0]] = step;

    return acc;
  }, {});

  const hasUnfinishedSteps = Object.values(unfinishedSteps).length;

  if (hasUnfinishedSteps)
    return (
      <div className="p-8">
        <h1 className="text-sm font-medium mb-2">
          Please complete the following steps to finish your application and
          proceed to check out.
        </h1>
        <div className="space-y-2">
          {Object.keys(unfinishedSteps).map((s) => {
            return (
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.XS}
                key={s}
                className="!block"
                onClick={() => {
                  setActiveStepName(s);
                }}
              >
                {StepNames[s]}
              </Button>
            );
          })}
        </div>
      </div>
    );

  if (sendDisclosureEmail && !isSubmissionDisclosureFinished)
    return (
      <div className="p-8 h-[calc(80vh-73px)] flex items-center justify-center bg-background-900 rounded-br-lg">
        <div>
          <div className="flex justify-center mb-6">
            <HugeiconsIcon
              icon={Clock01Icon}
              size={48}
              color="var(--primary-500)"
            />
          </div>

          <h1 className="text-lg font-bold text-content-1000 mb-2 text-center">
            Waiting for applicant to sign disclosure
          </h1>

          <p className="text-base text-content-400 text-center">
            The disclosure has been sent. Checkout will be available once
            it&apos;s signed
          </p>
        </div>
      </div>
    );

  const isStripeFieldsNull = (pi: PaymentIntent | null | undefined) =>
    pi &&
    !pi.client_secret &&
    !pi.payment_intent_id &&
    !pi.ephemeral_key &&
    !pi.stripe_customer_id;

  // Check for free checkout - either zero fee, cash payment, or null Stripe fields
  const isFreeCheckout =
    ((applicant.application_fee?.cents === 0 ||
      applicant.preferred_checkout_type ===
        SubmissionType.CheckoutPaidInCash) &&
      applicant.application_status !== ApplicantStatus.Submitted) ||
    isStripeFieldsNull(paymentIntent);

  if (isFreeCheckout)
    return (
      <FreeCheckoutView
        isFeeWaived={Boolean(isStripeFieldsNull(paymentIntent))}
        isPaidInCash={
          applicant.preferred_checkout_type ===
          SubmissionType.CheckoutPaidInCash
        }
        isUpdatingPreferredCheckoutType={isUpdatingPreferredCheckoutType}
        isSubmitting={freeCheckoutMeta.isPending}
        onGoBack={() =>
          updatePreferredCheckoutType({
            preferred_checkout_type: null
          }).catch(() => {
            toast.error("An error occurred while going back");
          })
        }
        onSubmit={() => {
          freeCheckoutMeta.mutateAsync();
        }}
      />
    );

  if (
    [ApplicationFeeStatus.Paid, ApplicationFeeStatus.NoApplicationFee].includes(
      applicant.application_fee_status
    )
  )
    return (
      <ApplicationFeePaidView
        applicant={applicant}
        templateCosts={templateCosts}
        propertyBankAccount={application.property?.bank_account}
        onFinish={onSuccess}
      />
    );

  if (applicant.preferred_checkout_type === SubmissionType.SendByEmail)
    return (
      <EmailPaymentPendingView
        isUpdatingPreferredCheckoutType={isUpdatingPreferredCheckoutType}
        onCancel={() => {
          updatePreferredCheckoutType({
            preferred_checkout_type: null
          }).catch(() => {
            toast.error("An error occurred while cancelling");
          });
        }}
        onFinish={onSuccess}
      />
    );

  // Should'nt be possible, but just in case
  if (!applicantId || !paymentIntent || isLoadingTemplateCosts) return null;

  if (isLoading)
    return (
      <div className="mt-20 mx-auto">
        <Loader />
      </div>
    );

  // Only show error if client_secret is missing but other Stripe fields exist (partial/broken payment intent)
  if (
    !paymentIntent.client_secret &&
    (paymentIntent.payment_intent_id ||
      paymentIntent.ephemeral_key ||
      paymentIntent.stripe_customer_id)
  )
    return (
      <div className="p-8 h-[calc(80vh-73px)] flex items-center justify-center">
        <div>
          <div className="h-20 w-20 flex items-center justify-center mx-auto bg-danger-50 rounded-full">
            <InfoCircle className="stroke-danger-500 h-10 w-10 rotate-180" />
          </div>
          <h1 className="text-xl font-bold text-center mt-4">
            Unable to checkout
          </h1>
          <p className="text-gray-500 text-center mt-2 text-base">
            {applicant.ineligible_to_checkout_reason ||
              "Please make sure there are no errors in identity steps"}
          </p>
          <Button
            variant={ButtonVariant.PRIMARY}
            className="mx-auto mt-10"
            onClick={() => {
              intercom.show();
            }}
          >
            Contact support
          </Button>
        </div>
      </div>
    );

  if (!applicant.preferred_checkout_type)
    return (
      <CheckoutOptionsSelector
        applicant={applicant}
        templateCosts={templateCosts}
        propertyBankAccount={application.property?.bank_account}
        updatePreferredCheckoutType={updatePreferredCheckoutType}
        isUpdatingPreferredCheckoutType={isUpdatingPreferredCheckoutType}
      />
    );

  // Only render Stripe Elements if we have a valid client_secret
  if (!paymentIntent.client_secret) return null;

  return (
    <div className="p-6 max-w-[480px] w-full relative mx-auto h-[calc(80vh-100px)] overflow-y-auto">
      <Elements
        options={{
          clientSecret: paymentIntent.client_secret,
          locale: "en"
        }}
        stripe={stripe}
      >
        <CheckoutForm
          checkout={(intentId) =>
            checkOutMeta.mutateAsync({ stripe_token: intentId })
          }
        />
      </Elements>
    </div>
  );
};
